import { Holidays } from "../holidays";
import { isDayString, shiftDay, weekdayOf } from "./day-string";
import { moment } from "./moment";

/**
 * The region whose public holidays count as days off.
 *
 * One value for the whole application, because the capacity board shades its columns by it and a
 * ticket's estimate is validated against it — the two disagreeing would let a form accept work the
 * board cannot draw.
 */
const HOLIDAY_REGION = "BE";

/**
 * The weekdays worked where nobody has said otherwise: Monday to Friday.
 *
 * Offsets, not names, and Monday is 0 — the locale sets `dow: 1`, so `moment().weekday()` counts
 * from Monday, which is also what the `Weekdays` picker emits and what `tour_waves.weekdays`,
 * `projects.weekdays` and `crew_definitions.weekdays` all store. Reading one of those columns with
 * `isoWeekday` instead is off by one and silently moves every day of the week.
 */
export const DEFAULT_WEEKDAYS = [0, 1, 2, 3, 4];

/**
 * The public holidays of each year asked about so far, as stored dates.
 *
 * `feiertagejs` recomputes the whole year and converts every date through the Berlin time zone on
 * each `isHoliday` call, which costs a quarter of a millisecond — and the capacity board asks the
 * question once per day per bar it draws, thousands of times per render. A year's holidays are a
 * dozen fixed dates, so they are computed once and the question becomes a set lookup.
 */
const holidaysByYear = new Map<number, Set<string>>();

/**
 * Whether a day is a public holiday in the region the application observes.
 *
 * @param date - The day, as a stored `YYYY-MM-DD` string.
 * @returns Whether nobody works it, whatever the weekday list says.
 */
export function isHoliday(date: string): boolean {
	const year = Number(date.slice(0, 4));
	let holidays = holidaysByYear.get(year);
	if (!holidays) {
		holidays = new Set(
			Holidays.getHolidays(year, HOLIDAY_REGION).map((holiday) => holiday.dateString),
		);
		holidaysByYear.set(year, holidays);
	}
	return holidays.has(date);
}

/**
 * Whether a day is one somebody works: its weekday is worked, and it is not a public holiday.
 *
 * **A holiday is never worked, whatever the weekday list says.** The list is a decision about a
 * site or a crew; a holiday is a fact about the calendar, and a site that works Saturdays does not
 * thereby work Christmas.
 *
 * @param date - The day, as a stored `YYYY-MM-DD` string. Anything else moment can read is
 *   normalised to one first.
 * @param weekdays - The weekdays worked, Monday-first offsets. Defaults to Monday to Friday.
 * @returns Whether work happens on it.
 */
export function isWorkingDay(date: string, weekdays: number[] = DEFAULT_WEEKDAYS): boolean {
	const day = isDayString(date) ? date : moment(date).db();
	if (!weekdays.includes(weekdayOf(day))) return false;
	return !isHoliday(day);
}

/**
 * How many working days a span holds, both ends included.
 *
 * This is the ceiling on any estimate measured in working days: a Friday-to-Sunday window is three
 * calendar days and holds exactly one under a Monday-to-Friday week, so an estimate of three there
 * describes work the window cannot contain.
 *
 * @param startDate - The span's first day, as a stored `YYYY-MM-DD` string.
 * @param endDate - The span's last day, inclusive.
 * @param weekdays - The weekdays worked, Monday-first offsets. Defaults to Monday to Friday.
 * @returns The number of working days, or 0 when the span is inverted.
 */
export function countWorkingDays(
	startDate: string,
	endDate: string,
	weekdays: number[] = DEFAULT_WEEKDAYS,
): number {
	if (endDate < startDate) return 0;

	let count = 0;
	for (let cursor = startDate; cursor <= endDate; cursor = shiftDay(cursor, 1)) {
		if (isWorkingDay(cursor, weekdays)) count += 1;
	}
	return count;
}
